import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2, MessageCircleQuestion, Music4 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = {
  questions: {
    label: "Questions",
    icon: MessageCircleQuestion,
    welcome:
      "Bonjour ! Je suis l'assistant KLMProd. Formules, tarifs, organisation du jour J… posez-moi vos questions.",
    suggestions: [
      "Quelle formule pour 120 invités ?",
      "Comment fonctionne la playlist collaborative ?",
      "Quand faut-il verser l'acompte ?",
    ],
  },
  idees: {
    label: "Idées musicales",
    icon: Music4,
    welcome:
      "Parlez-moi de votre ambiance rêvée : je vous suggère des titres pour l'entrée des mariés, l'ouverture du bal, le gâteau, l'apéritif…",
    suggestions: [
      "Une entrée des mariés pleine d'énergie",
      "Une ouverture de bal romantique",
      "Ambiance apéritif jazzy et feutrée",
    ],
  },
};

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("questions");
  const [model, setModel] = useState("claude");
  const [messages, setMessages] = useState({ questions: [], idees: [] });
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(crypto.randomUUID());
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => ({
      ...m,
      [mode]: [...m[mode], { role: "user", content }, { role: "assistant", content: "" }],
    }));

    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, mode, model, message: content }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finished = false;
      while (!finished) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6).trim();
          if (payload === "[DONE]") {
            finished = true;
            break;
          }
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) {
              setMessages((m) => {
                const list = [...m[mode]];
                list[list.length - 1] = {
                  role: "assistant",
                  content: list[list.length - 1].content + parsed.delta,
                };
                return { ...m, [mode]: list };
              });
            } else if (parsed.error) {
              setMessages((m) => {
                const list = [...m[mode]];
                list[list.length - 1] = {
                  role: "assistant",
                  content: "Désolé, je suis momentanément indisponible. Réessayez dans un instant.",
                };
                return { ...m, [mode]: list };
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const list = [...m[mode]];
        list[list.length - 1] = {
          role: "assistant",
          content: "Désolé, la connexion a échoué. Réessayez dans un instant.",
        };
        return { ...m, [mode]: list };
      });
    } finally {
      setStreaming(false);
    }
  };

  const current = MODES[mode];
  const history = messages[mode];

  return (
    <>
      <motion.button
        data-testid="assistant-open-button"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: open ? 0 : 1, scale: open ? 0.8 : 1 }}
        className="bg-brand-gradient fixed bottom-4 left-4 z-[80] flex h-12 w-12 items-center justify-center rounded-full text-[#04121f] shadow-[0_8px_30px_rgba(0,150,255,0.45)] transition-transform duration-300 hover:scale-110 sm:bottom-6 sm:left-6 sm:h-14 sm:w-14"
        aria-label="Ouvrir l'assistant KLMProd"
        style={{ pointerEvents: open ? "none" : "auto" }}
      >
        <Sparkles size={24} />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="assistant-panel"
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-[4.25rem] left-4 right-4 z-[80] flex h-[min(540px,calc(100dvh-5.5rem))] flex-col overflow-hidden rounded-3xl border border-cyan-500/25 bg-[#0E1524]/95 shadow-[0_30px_80px_rgba(0,0,0,0.5)] backdrop-blur-xl sm:bottom-24 sm:left-6 sm:right-auto sm:w-[400px]"
          >
            <div className="flex items-center justify-between border-b border-cyan-500/15 px-5 py-4">
              <div>
                <p className="font-display text-sm font-extrabold text-slate-50">Assistant KLMProd</p>
                <p className="text-[11px] text-slate-500">
                  Propulsé par {model === "gpt" ? "ChatGPT" : "Claude"} (IA)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-0.5 rounded-full border border-cyan-500/20 p-0.5" data-testid="assistant-model-switch">
                  {[
                    ["claude", "Claude"],
                    ["gpt", "ChatGPT"],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      data-testid={`assistant-model-${key}`}
                      onClick={() => setModel(key)}
                      className={`rounded-full px-2.5 py-1 text-[10px] font-bold transition-colors duration-300 ${
                        model === key ? "bg-brand-gradient text-[#04121f]" : "text-slate-500 hover:text-cyan-300"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="assistant-close-button"
                  onClick={() => setOpen(false)}
                  className="text-slate-400 transition-colors hover:text-slate-100"
                  aria-label="Fermer l'assistant"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="flex gap-2 border-b border-cyan-500/10 px-4 py-3">
              {Object.entries(MODES).map(([key, m]) => (
                <button
                  key={key}
                  data-testid={`assistant-mode-${key}`}
                  onClick={() => setMode(key)}
                  className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                    mode === key
                      ? "bg-brand-gradient text-[#04121f]"
                      : "border border-cyan-500/20 text-slate-400 hover:text-cyan-300"
                  }`}
                >
                  <m.icon size={13} />
                  {m.label}
                </button>
              ))}
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="assistant-messages">
              <div className="rounded-2xl rounded-tl-sm border border-cyan-500/15 bg-[#080C14]/70 px-4 py-3 text-sm leading-relaxed text-slate-300">
                {current.welcome}
              </div>

              {history.length === 0 && (
                <div className="flex flex-wrap gap-2">
                  {current.suggestions.map((s) => (
                    <button
                      key={s}
                      data-testid={`assistant-suggestion-${s.slice(0, 20).toLowerCase().replace(/[^a-z]/g, "-")}`}
                      onClick={() => send(s)}
                      className="rounded-full border border-cyan-400/25 px-3 py-1.5 text-xs text-cyan-300 transition-colors hover:bg-cyan-400/10"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {history.map((msg, i) => (
                <div
                  key={i}
                  data-testid={`assistant-message-${i}`}
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "ml-auto rounded-br-sm bg-brand-gradient font-medium text-[#04121f]"
                      : "rounded-tl-sm border border-cyan-500/15 bg-[#080C14]/70 text-slate-200"
                  }`}
                >
                  {msg.content}
                  {msg.role === "assistant" && msg.content === "" && streaming && (
                    <Loader2 size={15} className="animate-spin text-cyan-400" />
                  )}
                </div>
              ))}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-cyan-500/15 p-3"
            >
              <input
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={mode === "idees" ? "Décrivez votre ambiance…" : "Votre question…"}
                className="flex-1 rounded-full border border-cyan-500/20 bg-[#080C14]/70 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
              />
              <button
                type="submit"
                data-testid="assistant-send-button"
                disabled={streaming || !input.trim()}
                className="bg-brand-gradient flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#04121f] transition-transform duration-300 hover:scale-105 disabled:opacity-50"
                aria-label="Envoyer"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
